import type {
  CheckoutSessionResponse,
  PaymentInstitutionsResponse,
  PublicBillPaymentResponse,
  PublicInvoiceResponse,
} from "./public";

// Two colours chosen to exercise contrast branches:
// - a saturated dark teal that reads well as foreground and can tint freely
// - a very pale cream that must fall back to neutral for amount-due / button text
const DARK_BRAND = "#0F766E";
const LIGHT_BRAND = "#FEF3C7";

const basePayable: PublicInvoiceResponse = {
  documentNumber: "INV-00042",
  currency: "GBP",
  totalAmount: 124.5,
  amountPaid: 0,
  amountCredited: 0,
  amountDue: 124.5,
  issueDate: new Date().toISOString(),
  dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
  status: "Issued",
  memo: "Thanks for your business — please pay on time.",
  customerName: "Joe Bloggs",
  organisationName: "ACME Electric Ltd",
  organisationLogoUrl: null,
  organisationPrimaryColour: DARK_BRAND,
  whatsAppOptInAvailable: true,
  whatsAppOptedIn: false,
};

export type MockState =
  | "payable"
  | "payable-light"
  | "paid"
  | "voided"
  | "partial"
  | "credited"
  | "early-payment"
  | "invalid"
  | "network-error";

export const MOCK_STATES: MockState[] = [
  "payable",
  "payable-light",
  "paid",
  "voided",
  "partial",
  "credited",
  "early-payment",
  "invalid",
  "network-error",
];

export function isMockState(value: string | null | undefined): value is MockState {
  return !!value && (MOCK_STATES as string[]).includes(value);
}

export function mockInvoice(state: MockState): PublicInvoiceResponse | null {
  switch (state) {
    case "payable":
      return basePayable;
    case "payable-light":
      return { ...basePayable, organisationPrimaryColour: LIGHT_BRAND, organisationName: "Sunrise Bakery" };
    case "paid":
      return {
        ...basePayable,
        status: "Paid",
        amountPaid: basePayable.totalAmount,
        amountDue: 0,
      };
    case "voided":
      return { ...basePayable, status: "Void" };
    case "partial":
      return {
        ...basePayable,
        status: "PartiallyPaid",
        amountPaid: 50,
        amountDue: basePayable.totalAmount - 50,
      };
    case "credited":
      return {
        ...basePayable,
        status: "Credited",
        amountCredited: basePayable.totalAmount,
        amountDue: 0,
      };
    case "early-payment":
      // The spec's worked example: £1,200 with 2% off if paid within 10 days.
      return {
        ...basePayable,
        totalAmount: 1200,
        amountDue: 1200,
        earlyPaymentDiscount: {
          amount: 24,
          expiresOn: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
          amountDueNow: 1176,
        },
      };
    case "invalid":
    case "network-error":
      return null;
  }
}

export function mockCheckoutSession(state: MockState): CheckoutSessionResponse | null {
  const invoice = mockInvoice(state);
  if (!invoice) return null;
  // Intentionally bogus values — mock mode never calls Stripe.js with these.
  return {
    clientSecret: "pi_mock_secret_123",
    publishableKey: "pk_test_mock",
    connectedAccountId: "acct_mock",
    amount: invoice.earlyPaymentDiscount?.amountDueNow ?? invoice.amountDue,
    currency: invoice.currency,
  };
}

// Bill payment link (/p/:token?mock=<state>)

const baseBillPayment: PublicBillPaymentResponse = {
  organisationName: "Sunrise Bakery Ltd",
  clientLogoUrl: null,
  clientPrimaryColour: DARK_BRAND,
  supplierName: "Northern Flour Mills",
  invoiceNumber: "NFM-20931",
  dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  currency: "GBP",
  totalAmount: 1840.0,
  outstanding: 1840.0,
  state: "Approved",
  payeeName: "Northern Flour Mills Ltd",
  payeeAccountNumberLast4: "4821",
  unavailableReason: null,
  unavailableMessage: null,
  latestPayment: null,
  disclosure: {
    text: "We have partnered with Yapily Connect to securely initiate payment from your account",
    termsUrl: "https://www.yapily.com/legal/end-user-terms",
    privacyUrl: "https://www.yapily.com/legal/privacy-policy",
  },
};

export type BillPaymentMockState =
  | "payable"
  | "payable-eur"
  | "unavailable"
  | "in-progress"
  | "pending-multi-auth"
  | "paid"
  | "voided"
  | "failed"
  | "invalid"
  | "network-error";

export const BILL_PAYMENT_MOCK_STATES: BillPaymentMockState[] = [
  "payable",
  "payable-eur",
  "unavailable",
  "in-progress",
  "pending-multi-auth",
  "paid",
  "voided",
  "failed",
  "invalid",
  "network-error",
];

export function isBillPaymentMockState(value: string | null | undefined): value is BillPaymentMockState {
  return !!value && (BILL_PAYMENT_MOCK_STATES as string[]).includes(value);
}

export function mockBillPayment(state: BillPaymentMockState): PublicBillPaymentResponse | null {
  const attempt = (status: NonNullable<PublicBillPaymentResponse["latestPayment"]>["status"], errorMessage: string | null = null) => ({
    status,
    errorMessage,
    createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
  });
  switch (state) {
    case "payable":
      return baseBillPayment;
    case "payable-eur":
      return {
        ...baseBillPayment,
        currency: "EUR",
        payeeAccountNumberLast4: "2606",
        payerAccounts: [
          { id: "acc-eur-1", name: "Business Euro", institutionId: "commerzbank", institutionName: "Commerzbank", last4: "3000", currency: "EUR" },
        ],
      };
    case "unavailable":
      return {
        ...baseBillPayment,
        unavailableReason: "BankPayment.SupplierBankDetailsNotVerified",
        unavailableMessage: "The supplier's bank details must be verified before paying by bank.",
      };
    case "in-progress":
      return { ...baseBillPayment, state: "PaymentInProgress", latestPayment: attempt("Authorised") };
    case "pending-multi-auth":
      return { ...baseBillPayment, state: "PaymentInProgress", latestPayment: attempt("Pending") };
    case "paid":
      return { ...baseBillPayment, state: "Paid", outstanding: 0, latestPayment: attempt("Completed") };
    case "voided":
      return { ...baseBillPayment, state: "Voided" };
    case "failed":
      return {
        ...baseBillPayment,
        state: "PaymentFailed",
        latestPayment: attempt("Failed", "Payment rejected by the bank"),
      };
    case "invalid":
    case "network-error":
      return null;
  }
}

// Ten UK banks and two eurozone ones, so the search box (shown past eight) appears and each currency greys the
// other's banks. Commerzbank needs the payer account for EUR. No icons: mock mode stays offline.
export const mockBillPaymentInstitutions: PaymentInstitutionsResponse = {
  institutions: [
    "Barclays",
    "HSBC UK",
    "Lloyds Bank",
    "Monzo",
    "NatWest",
    "Nationwide",
    "Revolut",
    "Santander UK",
    "Starling Bank",
    "TSB",
  ]
    .map((name) => ({
      id: name.toLowerCase().replace(/\s+/g, "-"),
      name,
      fullName: null,
      countries: ["GB"],
      iconUrl: null,
      supportedCurrencies: ["GBP"],
      payerAccountRequired: false,
    }))
    .concat([
      { id: "bnp-paribas", name: "BNP Paribas", fullName: null, countries: ["FR"], iconUrl: null, supportedCurrencies: ["EUR"], payerAccountRequired: false },
      { id: "commerzbank", name: "Commerzbank", fullName: null, countries: ["DE"], iconUrl: null, supportedCurrencies: ["EUR"], payerAccountRequired: true },
    ]),
  disclosure: baseBillPayment.disclosure,
};
